import React from 'react';
import { Link, Icon } from '@common/components';
import { gql, useMutation } from '@common/hooks';
import { Form, Input, Button, Radio, Row, Col } from 'antd';
import Auth from 'works/legacy/lib/auth';
import { Colors } from 'works/legacy/lib/style-helper';
import { defaultLoginRedirectPath } from 'works/legacy/lib/default-login-redirect-path';
import { useRedirectPath } from '@identity/hooks/use-redirect-path';
import { randomAccountColor } from '@identity/services';
import { MembershipType, UserType } from '@identity/graphql';
import moment from 'moment';
import { useQueryParam } from 'works/legacy/services/query-params-service';
import { REDIRECT_PATH_LABEL_KEY } from 'works/legacy/lib/redirect-to-login';

const SIGN_IN_WORKS_USER = gql`
  mutation SignInWorksUser($input: SignInWorksUserInput!) {
    signInWorksUser(input: $input) {
      worksUser {
        id
        name
      }
      worksMembership {
        id
        name
        entityType
        saveLastVisitedPage
        roleName
        entity: entityProvider {
          ... on Organization {
            id
            name
          }
          ... on Supplier {
            id
            name
          }
        }
      }
      token
      identityToken
    }
  }
`;

type Props = {
  email: string;
  user: UserType;
  memberships: MembershipType[];
};

export function SignInPasswordForm({ email, user, memberships }: Props) {
  const [form] = Form.useForm();
  const [signInWorksUser, { loading }] = useMutation(SIGN_IN_WORKS_USER);
  const redirectPath = useRedirectPath();
  const redirectPathLabelEncoded = useQueryParam(REDIRECT_PATH_LABEL_KEY) || '';
  const redirectPathLabel = decodeURIComponent(redirectPathLabelEncoded);

  const onFinish = (values: { password: string; membershipId: string }) => {
    signInWorksUser({
      variables: {
        input: {
          email: email,
          password: values.password,
          worksMembershipId: values.membershipId || memberships[0].id,
        },
      },
    }).then((result) => {
      if (result.errors) {
        console.error('sign in error:', result);

        form.setFields([
          {
            name: 'password',
            errors: ['Sign in failed. Check your password and try again.'],
          },
        ]);
        return;
      }

      const {
        token,
        identityToken,
        worksMembership: {
          id: membershipId,
          name,
          entityType,
          saveLastVisitedPage,
          entity: { name: entityName },
        },
      } = result.data.signInWorksUser;

      const userState = {
        token,
        identityToken,
        email,
        entityType,
        membershipId,
        name,
        entityName,
        saveLastVisitedPage,
        path: redirectPath || defaultLoginRedirectPath(entityType),
        pathLabel: redirectPathLabel,
        pathUpdatedAt: moment().toISOString(),
        color: randomAccountColor({ exclude: Auth.colorsBeingUsed() }),
      };

      Auth.store(userState);

      window.location.href = userState.path;
    });
  };

  const getEntityNameOrCode = (entity: { code: string; name: string }) => {
    if (user.internalTrustedUser && entity.code) {
      return entity.code;
    }

    return entity.name;
  };

  const initialValues = {
    membershipId: memberships[0]?.id,
  };

  return (
    <div className="sign-in-password-form">
      <Form form={form} onFinish={onFinish} initialValues={initialValues}>
        {memberships.length > 1 && (
          <div>
            <Form.Item name="membershipId">
              <Radio.Group size="large">
                {memberships.map((m) => (
                  <Radio key={m.id} value={m.id}>
                    {getEntityNameOrCode(m.entity)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>
        )}
        <Form.Item
          name="password"
          rules={[{ required: true, message: 'Please input your password!' }]}
        >
          <Input
            prefix={<Icon icon="lock" kind="light" />}
            type="password"
            placeholder="password"
            size="large"
            autoComplete="off"
          />
        </Form.Item>
        <Row align="middle">
          <Col span={12}>
            <Form.Item className="button-row">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="sign-in-form-button"
                size="large"
              >
                Sign In
              </Button>
            </Form.Item>
          </Col>
          <Col span={12} className="text-right">
            <Link to="/works/auth/forgot-password">Forgot Password</Link>
          </Col>
        </Row>
      </Form>
      <style jsx>
        {`
          .sign-in-password-form {
            width: 100%;
          }

          .sign-in-password-form :global(.sign-in-form-button) {
            margin-bottom: 0px;
            width: auto;
          }

          .sign-in-password-form :global(.ant-radio-wrapper) {
            display: block;
            height: 30px;
            line-height: 30px;
          }

          .sign-in-password-form :global(.ant-input-prefix) {
            color: ${Colors.steelDarker};
          }
          .sign-in-password-form :global(input) {
            padding-left: 0.4rem;
          }

          .sign-in-password-form :global(.button-row) {
            margin-bottom: 0;
          }
        `}
      </style>
    </div>
  );
}
